// @ts-nocheck
import React, { useMemo } from "react";
import { fmtDateTime, formatAnswerForTranscript } from "@/legacy/helpersModule";

const PHASE_ORDER = ["mandatory_open", "role", "ai_followup", "mandatory_close", "other"];

const PHASE_LABELS = {
  mandatory_open: "Mandatory opening",
  role: "Role-specific (HR script)",
  ai_followup: "AIRA follow-up (AI)",
  mandatory_close: "Mandatory closing",
  other: "Other",
};

/** Short prefix used for per-section question numbering (e.g. "Opening Q1"). */
const PHASE_SHORT = {
  mandatory_open: "Opening",
  role: "Role",
  ai_followup: "AI",
  mandatory_close: "Closing",
  other: "Q",
};

function normalizePhase(raw) {
  const p = String(raw || "").trim().toLowerCase();
  if (p === "mandatory_open" || p === "opening") return "mandatory_open";
  if (p === "mandatory_close" || p === "closing") return "mandatory_close";
  if (p === "ai_followup" || p === "ai") return "ai_followup";
  if (p === "role" || p === "hr") return "role";
  return "other";
}

/**
 * Classify a stored answer into a phase.
 * Source of truth (in priority order):
 *   1. questionPhase from the job question (mandatory_open / role / mandatory_close)
 *   2. questionId == null  → AI follow-up (AI questions are never linked to a saved job question)
 *   3. legacy fallback: text heuristics for old rows that have no phase
 */
function classifyAnswerPhase(a) {
  if (a.questionPhase) return normalizePhase(a.questionPhase);
  if (a.questionId == null) return "ai_followup";
  // Legacy scripted row (has a job question id but no stored phase): infer from text.
  const q = String(a.questionText || "").toLowerCase();
  if (/thank\s*you|thankyou|choosing us|have a great day/.test(q)) return "mandatory_close";
  if (
    /welcome|introduce yourself|motivated you to apply|key strengths|tell me about yourself/.test(q)
  ) {
    return "mandatory_open";
  }
  return "role";
}

/** Stable sort within a section: scripted by display order, AI by time asked. */
function sortWithinPhase(phase, items) {
  const out = items.map((a, i) => ({ a, i }));
  out.sort((x, y) => {
    if (phase === "ai_followup") {
      const tx = x.a.askedAt ? new Date(x.a.askedAt).getTime() : NaN;
      const ty = y.a.askedAt ? new Date(y.a.askedAt).getTime() : NaN;
      if (!Number.isNaN(tx) && !Number.isNaN(ty) && tx !== ty) return tx - ty;
      return x.i - y.i;
    }
    const dx = x.a.displayOrder;
    const dy = y.a.displayOrder;
    if (dx != null && dy != null && dx !== dy) return dx - dy;
    if (dx != null && dy == null) return -1;
    if (dx == null && dy != null) return 1;
    return x.i - y.i;
  });
  return out.map(({ a }) => a);
}

/**
 * Group stored interview answers into ordered phase sections.
 * Single source of truth: the `interview_answers` rows (already scoped to the
 * latest attempt by the API). Per-section numbering, fixed phase order.
 */
export function groupInterviewAnswersByPhase(answers) {
  const groups = new Map();
  (answers || []).forEach((a) => {
    const phase = classifyAnswerPhase(a);
    if (!groups.has(phase)) groups.set(phase, []);
    groups.get(phase).push({ ...a, _phase: phase });
  });
  return PHASE_ORDER.filter((p) => groups.has(p)).map((phase) => {
    const ordered = sortWithinPhase(phase, groups.get(phase)).map((a, i) => ({
      ...a,
      index: i + 1,
    }));
    return {
      phase,
      label: PHASE_LABELS[phase] || phase,
      shortLabel: PHASE_SHORT[phase] || "Q",
      items: ordered,
    };
  });
}

/**
 * Fallback: pair raw transcript chat lines into Q/A items when there are no
 * structured `interview_answers` rows. Used to recover the counter and PDF for
 * interviews whose per-question save failed but whose raw transcript_line rows
 * were persisted. Each AI line is treated as a question and the following
 * candidate line as its answer.
 */
export function buildSectionsFromChatLines(chatLines) {
  const lines = (chatLines || []).filter((l) => l && (l.text != null && l.text !== ""));
  if (!lines.length) return [];
  const items = [];
  let pendingQuestion = null;
  for (const line of lines) {
    const isAi = line.role === "ai" || line.role === "assistant";
    if (isAi) {
      if (pendingQuestion != null) {
        items.push({ questionText: pendingQuestion, answerText: "" });
      }
      pendingQuestion = line.text || "";
    } else if (pendingQuestion != null) {
      items.push({ questionText: pendingQuestion, answerText: line.text || "" });
      pendingQuestion = null;
    } else {
      items.push({ questionText: "", answerText: line.text || "" });
    }
  }
  if (pendingQuestion != null) {
    items.push({ questionText: pendingQuestion, answerText: "" });
  }
  if (!items.length) return [];
  return [
    {
      phase: "other",
      label: "Interview transcript",
      shortLabel: "Q",
      items: items.map((it, i) => ({ ...it, index: i + 1 })),
    },
  ];
}

function countSectionQuestions(sections) {
  return (sections || []).reduce((n, s) => n + (s.items?.length || 0), 0);
}

/**
 * Phase sections for the panel and PDF export.
 * Prefer structured `interview_answers` when they cover at least as many Q&A
 * pairs as the raw transcript. When transcript_line is fuller (e.g. scripted
 * saves failed but the end-of-interview chat backup succeeded), use chat lines
 * so HR and PDF show the complete conversation.
 */
export function buildTranscriptSections(answers, chatLines) {
  const structuredSections = groupInterviewAnswersByPhase(answers);
  const structuredCount = countSectionQuestions(structuredSections);
  const chatSections = buildSectionsFromChatLines(chatLines);
  const chatCount = countSectionQuestions(chatSections);

  if (structuredCount === 0) return chatSections;
  if (chatCount === 0) return structuredSections;
  if (chatCount > structuredCount) return chatSections;
  return structuredSections;
}

function Bubble({ role, text, sub }) {
  const isAi = role === "ai";
  return (
    <div className={`flex ${isAi ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[92%] sm:max-w-md p-3 rounded-2xl text-sm ${
          isAi ? "bg-slate-100 text-ink-900" : "bg-brand-600 text-white"
        }`}
      >
        <p className="text-xs uppercase font-black mb-1 opacity-50">
          {isAi ? "AIRA" : "Candidate"}
          {sub ? ` · ${sub}` : ""}
        </p>
        <p className="whitespace-pre-wrap break-words">{text || "—"}</p>
      </div>
    </div>
  );
}

export function InterviewTranscriptPanel({
  jobTitle,
  lang,
  loading,
  answers,
  chatLines,
  showHeader = true,
  collapsed,
  onToggleCollapsed,
}) {
  const sections = useMemo(
    () => buildTranscriptSections(answers, chatLines),
    [answers, chatLines],
  );

  const totalQuestions = sections.reduce((n, s) => n + s.items.length, 0);
  const msgCount = totalQuestions * 2;

  if (loading) {
    return <p className="text-sm text-ink-400">Loading full interview transcript…</p>;
  }

  if (!sections.length && !(chatLines && chatLines.length > 0)) {
    return (
      <p className="text-sm text-ink-400">No interview transcript stored for this application yet.</p>
    );
  }

  const body = (
    <div className="mt-4 space-y-6 max-h-[min(75vh,800px)] overflow-y-auto hide-scrollbar pr-1">
      {sections.map((section) => (
        <div key={section.phase} className="rounded-xl border border-line overflow-hidden">
          <div className="bg-slate-100 px-4 py-2.5 border-b border-line">
            <p className="text-xs font-black text-ink-600 uppercase tracking-wide">
              {section.label}
            </p>
            <p className="text-xs text-ink-400 mt-0.5">
              {section.items.length} question{section.items.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="p-4 space-y-4 bg-white">
            {section.items.map((a, idx) => (
              <div
                key={`${section.phase}-${a.index ?? idx}-${String(a.questionText || "").slice(0, 24)}`}
                className="space-y-2"
              >
                <Bubble role="ai" text={a.questionText} sub={`${section.shortLabel} Q${a.index ?? idx + 1}`} />
                <Bubble
                  role="user"
                  text={formatAnswerForTranscript(a.answerText, a.answerTextEn, lang)}
                />
                {a.askedAt ? (
                  <p className="text-[10px] text-ink-400 text-center">
                    {fmtDateTime(a.askedAt)}
                    {a.durationSeconds ? ` · ${a.durationSeconds}s` : ""}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
      {sections.length === 0 && chatLines?.length > 0 ? (
        <div className="space-y-3">
          {chatLines.map((e, i) => (
            <Bubble
              key={i}
              role={e.role === "ai" || e.role === "assistant" ? "ai" : "user"}
              text={e.text}
            />
          ))}
        </div>
      ) : null}
    </div>
  );

  if (!showHeader) return body;

  return (
    <div>
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="w-full flex items-center justify-between text-left gap-2"
      >
        <div>
          <h2 className="text-xs font-black text-ink-400 uppercase">
            Interview transcript · {jobTitle || "—"}
          </h2>
          <p className="text-xs text-ink-400 mt-0.5">
            {totalQuestions} questions · {msgCount} messages · {lang || "English"} — scroll for all
            sections
          </p>
        </div>
        <span className="text-brand-500 text-xs font-bold shrink-0">
          {collapsed ? "▼" : "▲"}
        </span>
      </button>
      {!collapsed ? body : null}
    </div>
  );
}
